/**
 * errors.ts — typed error classes for client-side branching.
 *
 * Today the only typed error is `OfflineError`. Routes that need to
 * differentiate "the network failed" from "the API returned a 4xx/5xx"
 * branch on `instanceof OfflineError`. Other API failures continue to
 * surface as the generic `ApiError` shape from `lib/api.ts`.
 *
 * Why a class (not a string code): `instanceof` survives across the
 * try/catch boundary in components without an extra `if (err.code === ...)`
 * pattern. It's also greppable for "show offline UI here."
 */

/**
 * Thrown when an API request can't reach the server because the device
 * is offline. Two trigger conditions:
 *
 *   1. `navigator.onLine === false` before the fetch is attempted.
 *      Cheapest detection — no network round-trip wasted.
 *
 *   2. fetch() rejects with `TypeError: Failed to fetch` (or similar
 *      browser variant). This catches captive portals, dead WiFi, and
 *      CGNAT failures that `navigator.onLine` misses (R3 in the plan).
 *
 * Routes catch this with `if (err instanceof OfflineError)` and render
 * the `<OfflineGate>` UI instead of "Load failed".
 */
export class OfflineError extends Error {
  constructor(message = 'offline') {
    super(message)
    this.name = 'OfflineError'
    // Preserve prototype chain across transpilation — required for
    // reliable `instanceof` checks on transpiled output (TypeScript
    // emits ES5 classes that lose the prototype without this).
    Object.setPrototypeOf(this, OfflineError.prototype)
  }
}

/**
 * Message safe to render to a student.
 *
 * OfflineError carries a diagnostic message ("timeout:GET /profile",
 * "offline-known:GET /discovery/recommendations") meant for logs. Pages that
 * did `err?.message ?? 'Erro ao carregar'` printed that verbatim, so students
 * on a dead mobile data path saw raw request internals in red.
 *
 * Gated routes should still prefer <OfflineGate>; this is the backstop for
 * every catch block that only has a string to show.
 */
export function userFacingError(err: unknown, fallback: string): string {
  if (err instanceof OfflineError) {
    return 'Sem internet. Liga-te para veres esta secção.'
  }
  const message = (err as { message?: unknown } | null)?.message
  return typeof message === 'string' && message.length > 0 ? message : fallback
}

/** Heuristic: is this fetch rejection a network failure (vs. a real
 *  HTTP-level error that already produced a Response)? Browsers throw
 *  `TypeError` from fetch when the request never reached a server.
 *  Some browsers vary the message ("Failed to fetch", "Load failed",
 *  "NetworkError when attempting to fetch resource") — match on the
 *  type, not the string. */
export function isNetworkFailure(err: unknown): boolean {
  return err instanceof TypeError
}
